import React, { useRef, useState } from 'react';
import VisuallyHidden from '@reach/visually-hidden';
import debounce from 'lodash/debounce';
import times from 'lodash/times';
import { Waypoint } from 'react-waypoint';
import { useEffectOnce } from 'react-use';
import { useCombobox } from 'downshift';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { AsyncStateItem, List, ListItem } from '../../ClientList.styles';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { ClientListClient, useClientListQuery } from '../../../../api/useClientListQuery';
import { ClientLoading } from './Client.loading';
import { SegmentedControl } from '../../../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { ClientType, DEBOUNCE_TIME } from '../../../../ClientSearch.meta';
import { ClientLozenge } from '../../../ClientLozenge/ClientLozenge';

const CLIENT_TYPES = [
  { label: 'All' },
  { value: ClientType.Client, label: 'Clients' },
  { value: ClientType.Prospect, label: 'Prospects' },
];

export const ClientListCombobox = () => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const [selectedClientType, setSelectedClientType] = useState<ClientType>();

  const { data, isFetching, hasNextPage, fetchNextPage } = useClientListQuery({
    search: searchTerm,
    type: selectedClientType,
  });

  const onSearch = (term?: string) => {
    setSearchTerm(term || undefined);
  };

  const debouncedOnSearch = useRef(debounce(onSearch, DEBOUNCE_TIME)).current;

  useEffectOnce(() => () => debouncedOnSearch.cancel());

  const clients = data?.pages
    ? data.pages.reduce((acc, page) => acc.concat(page.clients), [] as ClientListClient[])
    : [];

  const {
    getComboboxProps,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox({
    items: clients,
    itemToString: client => (client ? `${client.firstName} ${client.lastName}` : ''),
    onInputValueChange: ({ inputValue }) => debouncedOnSearch(inputValue),
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) {
        ADV.util.Router.go2('ConsumerOverview', { clientId: selectedItem.id });
      }
    },
  });

  return (
    <>
      <Box px="24" py="16" {...getComboboxProps()}>
        <VisuallyHidden as="label" {...getLabelProps()}>
          Search clients
        </VisuallyHidden>

        <Input leftIcon="far fa-search" placeholder="Search for a client" {...getInputProps()} />
      </Box>

      <Box px="24" pb="16">
        <SegmentedControl
          id="client-list--client-type"
          values={CLIENT_TYPES.map(({ label }) => label)}
          selectedIndex={CLIENT_TYPES.findIndex(({ value }) => value === selectedClientType)}
          onChange={index => setSelectedClientType(CLIENT_TYPES[index].value)}
          ariaLabel="Client type"
        />
      </Box>

      <List {...getMenuProps()}>
        {!isFetching && clients.length === 0 ? (
          <AsyncStateItem>
            <Text fontSize="h6" color="neutral.500">
              No results found
            </Text>
          </AsyncStateItem>
        ) : (
          <>
            {clients.map((client, index) => (
              <ListItem
                key={client.id}
                {...getItemProps({ item: client, index })}
                isHighlighted={highlightedIndex === index}
              >
                <Box flex={1}>
                  <Text fontSize="h6" fontWeight={600} isOverflowWrap>
                    {client.firstName} {client.lastName}
                  </Text>

                  {client.email && (
                    <Text fontWeight={500} color="neutral.500" isOverflowWrap>
                      {client.email}
                    </Text>
                  )}
                </Box>

                {!selectedClientType && <ClientLozenge clientType={client.type} />}
              </ListItem>
            ))}

            {isFetching && (
              <>
                {times(5).map(key => (
                  <ClientLoading key={key} />
                ))}
              </>
            )}

            {hasNextPage && <Waypoint onEnter={() => fetchNextPage()} bottomOffset={-100} />}
          </>
        )}
      </List>
    </>
  );
};
